<template>
  <div class="edit-form-list-item">
    <div class="history-item">
      <div class="history-item-button">
        <div class="button">
          <el-button
            :disabled="!(index > 0 && length > 1)"
            icon="el-icon-arrow-up"
            circle
            @click.stop="handleCommand('moveUp')"
          ></el-button>
        </div>
        <div class="button">
          <el-button
            :disabled="!(index + 1 != length && length > 1)"
            icon="el-icon-arrow-down"
            circle
            @click.stop="handleCommand('moveDown')"
          ></el-button>
        </div>
      </div>
      <div class="history-item-content">
        <el-form
          class="history-form"
          ref="historyItemForm"
          :model="editableItem"
          :rules="rules"
          @submit.native.prevent
        >
          <!-- <div class="history-form-date"> -->
          <div>
            <el-form-item prop="skillname">
              <div>
                <label for="skillname" class="item-name"
                  >スキルや資格の名称</label
                >
              </div>
              <el-input
                class="input-lg"
                type="textarea"
                id="skillname"
                :autosize="{ minRows: 1, maxRows: 4 }"
                :placeholder="descriptionPlaceholder"
                v-model="editableItem.skillname"
                ref="desc"
              ></el-input>
            </el-form-item>
            <el-form-item prop="years">
              <div><label for="years" class="item-name">経験年数</label></div>
              <el-input
                class="input-xxs"
                placeholder="3"
                title="年"
                id="years"
                v-model="editableItem.years"
                type="number"
                :min="0"
                :max="computedMaxYear"
                ref="year"
              >
              </el-input>
              <span>年</span>
            </el-form-item>
            <!-- </div> -->
          </div>
          <el-form-item prop="description">
            <div>
              <label for="description" class="item-name">習熟度・説明</label>
            </div>
            <el-input
              class="input-lg"
              type="textarea"
              id="desctipsion"
              :autosize="{ minRows: 1, maxRows: 4 }"
              :placeholder="descriptionPlaceholder2"
              v-model="editableItem.description"
              ref="desc"
            ></el-input>
          </el-form-item>
          <div class="errors" v-if="errors">
            <ul>
              <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
            </ul>
          </div>
          <div class="enter text-right">
            <el-button
              icon="el-icon-delete"
              @click.stop="handleCommand('delete')"
              >削除</el-button
            >
            <el-button icon="el-icon-check" @click.stop="deactivateHistoryItem"
              >OK</el-button
            >
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  ref
} from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'

type SkillItem = {
  skillname?: string
  years?: number | string
  description?: string
  uuid?: string
}

type FormLibWithCharCount = {
  charCount: (value: unknown) => number
}

type ElFormLike = {
  validate: () => Promise<unknown>
}

type ElInputLike = {
  resizeTextarea?: () => void
  $el?: { offsetHeight?: number }
}

const props = defineProps<{
  item: SkillItem
  index: number
  length: number
  descriptionPlaceholder?: string
  descriptionPlaceholder2?: string
  descriptionMaxLength?: number
  minYear?: number | string
  maxYear?: number | string
}>()

const emit = defineEmits<{
  (e: 'add-history-item', index: number, item?: SkillItem): void
  (e: 'delete-history-item', index: number): void
  (e: 'move-history-item', params: { index: number; to: string }): void
}>()

const { formLib } = useHostServices()
const formLibChars = formLib as typeof formLib & FormLibWithCharCount
const historyItemForm = ref<ElFormLike | null>(null)
const desc = ref<ElInputLike | ElInputLike[] | null>(null)
const errors = ref<string[]>([])

const editableItem = computed(() => props.item)
const computedMaxYear = computed(() =>
  props.maxYear ? Number(props.maxYear) : new Date().getFullYear() + 3
)

function charTooLong(value: unknown, max: number) {
  return formLibChars.charCount(value) > max
}

const rules = {
  year: [
    {
      validator: (
        _rule: unknown,
        value: unknown,
        callback: (err?: Error) => void
      ) => {
        if (charTooLong(value, 6)) callback(new Error('Please input digits'))
        else callback()
      },
      message: '年が長すぎます',
      trigger: ['change', 'blur']
    }
  ],
  month: [
    {
      validator: (
        _rule: unknown,
        value: unknown,
        callback: (err?: Error) => void
      ) => {
        if (charTooLong(value, 4)) callback(new Error('Please input digits'))
        else callback()
      },
      message: '月が長すぎます',
      trigger: ['change', 'blur']
    }
  ],
  description: [
    {
      validator: (
        _rule: unknown,
        value: unknown,
        callback: (err?: Error) => void
      ) => {
        if (charTooLong(value, props.descriptionMaxLength || 0)) {
          callback(new Error('Please input digits'))
        } else callback()
      },
      message:
        '一行におさまる文字数をオーバーしています。文章を短くするか、行を ＋ で追加して入力してください。',
      trigger: ['change', 'blur']
    }
  ]
}

function handleCommand(command: string) {
  if (command === 'addAbove') emit('add-history-item', props.index)
  else if (command === 'addBelow') emit('add-history-item', props.index + 1)
  else if (command === 'delete') emit('delete-history-item', props.index)
  else if (command === 'duplicate') {
    emit('add-history-item', props.index, props.item)
  } else if (command === 'moveUp') {
    emit('move-history-item', { index: props.index, to: 'up' })
  } else if (command === 'moveDown') {
    emit('move-history-item', { index: props.index, to: 'down' })
  }
}

function deactivateHistoryItem() {
  emitter.emit('deactivate-history-item')
}

function resizeDesc() {
  nextTick(() => {
    const target = Array.isArray(desc.value) ? desc.value[0] : desc.value
    if (target && typeof target.resizeTextarea === 'function') {
      if (target.$el && target.$el.offsetHeight) target.resizeTextarea()
    }
  })
}

onBeforeMount(() => {
  nextTick(() => {
    if (!historyItemForm.value) return
    historyItemForm.value
      .validate()
      .then()
      .catch(() => {})
  })
})

onMounted(() => {
  emitter.on('card-changed', resizeDesc)
})

onBeforeUnmount(() => {
  emitter.off('card-changed', resizeDesc)
})
</script>
<style scoped lang="scss">
$white: #fff;
$basecolor: #f8f8f8;
$light-gray: #dcdfe6;
$errorcolor: #f56c6c;

/* .projects { */

/* background-color:rgba(246,247,249,0.8); */

/* } */
.project__invisible {
  display: none;
}

.project__invisible2 {
  display: none;
}

.edit-form-list-item {
  &.active {
    .project-item {
      border-top: dashed 1px $light-gray;
      padding: 1rem 0;
    }
  }
}

.history-item {
  display: flex;
  flex-direction: row-reverse;
}

.history-item-content {
  flex: 1;
  margin: 0.5rem 0;
  padding: 0.5rem 1rem;

  .attention-limit {
    font-size: 0.75rem;
    color: $errorcolor;

    &.character {
      padding: 0.5rem 0;
    }

    &.column {
      background: $white;
      margin: 0.5rem 0;
      padding: 0.5rem 1rem;
    }
  }
}

/* 詳細を隠す時に上下ボタンが高さを確保してしまうので修正 */

/* buttonにdisplay: flex; 追加 */
.history-item-button {
  .button {
    display: flex;
  }
}

.list-move {
  transition: 500ms cubic-bezier(0.59, 0.12, 0.34, 0.95);
  transition-property: opacity, transform;
}
</style>
